import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import moment from 'moment'
import { IconEye, IconLock, IconPencil, IconTrash, IconUser } from '@tabler/icons-react'
import { useFlags } from 'launchdarkly-react-client-sdk'

import { Button } from '@/components/buttons/Button'
import { dateFormatMoment } from '@/utils/constants'
import { convertAgentNameToInitials } from '@/utils/helpers'

import { allowAgentVisibleFor } from './noteVisibility'
import { NoteForm } from './NoteForm'
import { renderMentions } from './renderMentions'

import './NoteCard.scss'

const VISIBILITY_META = {
  public: { Icon: IconEye, label: 'Public' },
  private: { Icon: IconLock, label: 'Private' },
  agent_visible: { Icon: IconUser, label: 'Visible to agent' },
}

/**
 * Renders one note in a Linear/Slack-style row: avatar + inline header
 * (author · relative time · visibility) + hanging-indent body, with
 * edit/delete revealed on hover. Author + org-1 admin can edit/delete.
 *
 * @param {object} props
 * @param {NoteResponse} props.note - shape from /api/notes
 * @param {number} props.organizationId
 * @param {Array<object>} [props.orgUsers=[]] - org user list (for @mention highlighting)
 * @param {(noteId: number, fields: object) => void} props.onPatch
 * @param {(noteId: number) => void} props.onDelete
 * @param {boolean} [props.editingDisabled=false]
 * @param {React.ReactNode} [props.source] - muted context line rendered under the body (e.g. the clickable "↪ Scorecard: …" jump in the call-level aggregate view)
 */
export const NoteCard = ({
  note,
  organizationId,
  orgUsers = [],
  onPatch,
  onDelete,
  editingDisabled = false,
  source = null,
}) => {
  const { noteMentions: mentionsEnabled = false } = useFlags()
  const { user_id: currentUserId, organizationid: viewerOrgId } = useSelector(
    (state) => state.currentUser
  )
  const [isEditing, setIsEditing] = useState(false)

  const canEdit = !editingDisabled && (note.created_by === currentUserId || viewerOrgId === 1)
  const visibility = VISIBILITY_META[note.visibility] || VISIBILITY_META.public
  const isEdited = note.edited_at && note.edited_at !== note.created_at
  // API returns naive UTC timestamps; parse as UTC then localize. Show
  // relative time for the first day, then fall back to the absolute date.
  const createdAt = moment.utc(note.created_at).local()
  const displayDate =
    moment().diff(createdAt, 'hours') < 24
      ? createdAt.fromNow()
      : createdAt.format(dateFormatMoment.long)
  const renderedBody = useMemo(
    () => (mentionsEnabled ? renderMentions(note.note_text, orgUsers) : note.note_text),
    [note.note_text, orgUsers, mentionsEnabled]
  )

  const handleSave = ({ noteText, visibility: newVisibility, mentionedUserIds }) => {
    onPatch(note.id, {
      note_text: noteText,
      visibility: newVisibility,
      mentioned_user_ids: mentionedUserIds,
    })
    setIsEditing(false)
  }

  if (isEditing) {
    return (
      <NoteForm
        organizationId={organizationId}
        orgUsers={orgUsers}
        mode="edit"
        autoFocus
        initialValues={{
          noteText: note.note_text,
          visibility: note.visibility,
          mentionedUserIds: note.mentioned_user_ids || [],
        }}
        // `note_type` is a required field on the workflow-service
        // NoteResponse model — never null in API output. Safe to
        // pass directly without a fallback; if it ever becomes
        // optional, allowAgentVisibleFor returns false and hides the
        // option (fail-safe direction).
        allowAgentVisible={allowAgentVisibleFor(note.note_type)}
        onSubmit={handleSave}
        onCancel={() => setIsEditing(false)}
      />
    )
  }

  return (
    <article className="note-card" data-testid={`note-card-${note.id}`}>
      <div className="note-card__avatar" aria-hidden="true">
        {convertAgentNameToInitials(note.author || '?')}
      </div>

      <div className="note-card__main">
        {/* div, not <header>: avoids .qa-score's bare `header` rule bleeding in */}
        <div className="note-card__header">
          <span className="note-card__author">{note.author || 'Unknown'}</span>
          <span className="note-card__timestamp" title={createdAt.format(dateFormatMoment.long)}>
            {displayDate}
            {isEdited && <span className="note-card__edited"> · edited</span>}
          </span>

          <span
            className="note-card__visibility"
            data-testid={`note-card-visibility-${note.id}`}
            title={visibility.label}
          >
            <visibility.Icon size={13} />
            {visibility.label}
          </span>

          {canEdit && (
            <div className="note-card__actions">
              <Button
                xs
                square
                onClick={() => setIsEditing(true)}
                data-testid={`note-card-edit-${note.id}`}
                tooltip="Edit note"
              >
                <IconPencil />
              </Button>
              <Button
                xs
                square
                critical
                onClick={() => onDelete(note.id)}
                data-testid={`note-card-delete-${note.id}`}
                tooltip="Delete note"
              >
                <IconTrash size={14} />
              </Button>
            </div>
          )}
        </div>

        <p className="note-card__body" data-testid={`note-card-body-${note.id}`}>
          {renderedBody}
        </p>

        {source && <div className="note-card__source">{source}</div>}
      </div>
    </article>
  )
}
